import { defineStore } from 'pinia';
import { StorageSerializers, useLocalStorage } from '@vueuse/core';
import { useBackendFetch } from '@/fetch';
import type { GoalPlan, LoginData } from '@/types';

export const useStore = defineStore('main', {
  state() {
    return {
      loggedIn: useLocalStorage('logged-in', false),
      userId: useLocalStorage('user-id', 0),
      firstName: useLocalStorage('user-first-name', ''),
      lastName: useLocalStorage('user-last-name', ''),
      company: useLocalStorage('user-company', ''),
      email: useLocalStorage('user-email', ''),
      token: useLocalStorage('user-token', ''),
      sex: useLocalStorage('user-sex', 'U'),
      profile: useLocalStorage('user-profile', null, {
        serializer: StorageSerializers.object,
      }),
      savePoints: useLocalStorage('save-points', null, {
        serializer: StorageSerializers.object,
      }),
      profiles: useLocalStorage<{ id: number; created_at: string }[]>(
        'user-profiles',
        [],
      ),
      feedbackQuestions: useLocalStorage<
        {
          name: string;
          text: string;
          options: { text: string; value: string }[];
        }[]
      >('feedback-questions', []),
      answerTexts: useLocalStorage<Record<number, Array<{ value: string; text: string }>>>('answer-texts', {}),
      categoryAnswers: useLocalStorage<Record<number, Record<string, string>>>('category-answers', {}),
      // Session management
      lastActivity: useLocalStorage('last-activity', 0),
      sessionTimeout: useLocalStorage('session-timeout', 4 * 60 * 60 * 1000), // 4 hours in milliseconds
      sessionWarningShown: useLocalStorage('session-warning-shown', false),
    };
  },
  actions: {
    setUserInfo(email: string) {
      const users: Record<string, string> = {
        'stefan.vonbelow@nobina.se': 'Stefan von Below',
        'robert.borger@nobina.se': 'Robert Borger',
        'sarah.elifari@nobina.se': 'Sarah Elifari',
        'susanna.lejeborg@nonina.se': 'Susanna Lejeborg',
        'eva.palsson@nobina.se': 'Eva Pålsson',
        'carolina.granstrom@nobina.se': 'Carolina Granström',
      };

      if (email.length > 0 && email in users) {
        this.firstName = users[email].split(' ')[0];
        this.lastName = users[email].split(' ')[1];
        this.company = 'Nobina';
        this.email = email;
      } else {
        this.firstName = 'Testare';
        this.company = 'Testföretag AB';
        this.email = 'test@foretag.se';
      }
    },
    login(data: LoginData) {
      this.loggedIn = true;
      this.token = data.token;
      this.userId = data.user.id;
      this.firstName = data.user.first_name;
      this.lastName = data.user.last_name;
      this.email = data.user.email;
      this.company = data.user.company;
      this.profile = data.user.active_profile;
      this.sex = data.user.sex;
      this.profiles = data.user.finished_profiles;
      this.feedbackQuestions = [];
    },
    logout() {
      this.loggedIn = false;
      this.userId = 0;
      this.firstName = '';
      this.lastName = '';
      this.company = '';
      this.email = '';
      this.token = '';
      this.profile = null;
      this.savePoints = null;
      this.profiles = [];
      this.feedbackQuestions = [];
    },
    hasAnyValues() {
      if (this.profile == null || this.profile?.values == null) {
        return false;
      }

      return Object.values(this.profile?.values).length > 0;
    },
    /**
     * Refresh the active profile from the server.
     * This ensures we always have the latest profile state.
     * Returns true if there's an active profile, false otherwise.
     */
    async refreshActiveProfile(): Promise<boolean> {
      // If we have a local profile, verify it's still active via the status endpoint
      if (this.profile?.id) {
        return this.checkProfileActive();
      }
      
      // No local profile - try to get one from /auth/me
      try {
        const { data, statusCode } = await useBackendFetch('/auth/me')
          .get()
          .json();

        if (statusCode.value === 401) {
          this.logout();
          return false;
        }

        if (statusCode.value !== 200 || !data.value) {
          return false;
        }

        const user = data.value.user || data.value;
        
        // Update profile from server
        if (user.active_profile) {
          this.profile = user.active_profile;
          this.profiles = user.finished_profiles || [];
          return true;
        } else {
          // No active profile from server
          this.profiles = user.finished_profiles || [];
          return false;
        }
      } catch (error) {
        console.error('Error refreshing profile:', error);
        return false;
      }
    },
    /**
     * Check if the current profile is still active (in_progress).
     * Returns true if active, false if finished or not found.
     * If profile is finished, clears the local profile and returns false.
     */
    async checkProfileActive(): Promise<boolean> {
      if (!this.profile?.id) {
        return false;
      }

      try {
        const { data, statusCode } = await useBackendFetch(
          `/profile/${this.profile.id}/status`,
        )
          .get()
          .json();

        if (statusCode.value === 401) {
          this.logout();
          return false;
        }

        if (statusCode.value !== 200 || !data.value) {
          return false;
        }

        // If profile is not in_progress, clear local profile
        if (!data.value.in_progress) {
          console.log('Profile is no longer active, clearing local state');
          this.profile = null;
          this.savePoints = null;
          // Update finished profiles list if provided
          if (data.value.finished_profiles) {
            this.profiles = data.value.finished_profiles;
          }
          return false;
        }

        return true;
      } catch (error) {
        console.error('Error checking profile status:', error);
        return true; // Assume active on error to avoid blocking user
      }
    },
    getProfileValue(name: string) {
      return this.profile?.values?.[name] ?? null;
    },
    getProfileFactor(category: number) {
      return this.profile?.factors?.[category] ?? null;
    },
    async setProfileValue(name: string, value: any) {
      // Early safety check for profile
      if (!this.profile?.id) {
        console.error('No active profile to save to');
        this.logout();
        return null;
      }

      try {
        // Log the exact data being sent to the API
        // console.log(
        //   `Saving value for ${name}: ${value} to profile ${this.profile.id}`,
        // );

        const { data, statusCode } = await useBackendFetch(
          `/profile/${this.profile.id}/value`,
        )
          .post({ name, value })
          .json();

        // Handle authentication errors
        if (statusCode.value === 401) {
          console.error('Authentication error when saving profile value');
          this.logout();
          return null;
        }

        // Handle general API errors
        if (!data.value) {
          console.error('No data returned from API', data);
          return null;
        }

        // First update the profile values from the response
        if (
          data.value.profile_values &&
          Array.isArray(data.value.profile_values)
        ) {
          for (const { name, value } of data.value.profile_values) {
            // Ensure we're working with the actual value, not a proxy
            this.profile.values[name] = value;
            // console.log(`Updated profile value: ${name} = ${value}`);
          }
        }

        // Then update all the factors that were returned
        if (
          data.value.profile_factors &&
          Array.isArray(data.value.profile_factors)
        ) {
          for (const {
            category_id,
            improve,
            status,
            value,
            label,
            risk_value,
          } of data.value.profile_factors) {
            if (value === null && !category_id) {
              continue;
            }

            // Create the factor object with all necessary properties
            this.profile.factors[category_id] = {
              improve,
              status: status?.replace('profile.', '') || '',
              value,
              label,
              risk_value,
            };
            // console.log(
            //   `Updated factor ${category_id} with value ${value}, risk_value ${risk_value}`,
            // );
          }
        }

        // Handle single factor update
        if (data.value.category_id && data.value.risk_status_name) {
          this.profile.factors[data.value.category_id] = {
            label: data.value.risk_status_label || '',
            status: data.value.risk_status_name?.replace('profile.', '') || '',
            value: data.value.value,
            risk_value: data.value.risk_value,
          };
          // console.log(
          //   `Updated single factor ${data.value.category_id} with value ${data.value.value}`,
          // );
        }

        // Update fitness text if provided
        if (data.value.fitness_text !== undefined) {
          this.profile.fitness_text = data.value.fitness_text;
        }

        // Handle energy values if provided
        if ('energy_balance_value' in data.value) {
          this.profile.values['foodEnergyIntake'] =
            data.value.energy_intake_value;
          this.profile.values['foodEnergyBalance'] =
            data.value.energy_balance_value;
        }

        // Handle physical activity factor if provided
        if (data.value.physical_activity_factor) {
          const paf = data.value.physical_activity_factor;
          this.profile.factors[paf.category_id] = {
            improve: paf.improve,
            label: paf.label || '',
            status: paf.status ? paf.status.replace('profile.', '') : '',
            value: paf.value,
            risk_value: paf.value,
          };
        }

        // Force reactivity update by creating a fresh copy of values object
        // This helps in cases where Vue might not detect all nested changes
        if (this.profile.values) {
          const freshValues = { ...this.profile.values };
          this.profile.values = freshValues;
        }

        return data.value;
      } catch (error) {
        console.error('Error saving profile value:', error);
        return null;
      }
    },
    async setImproveValue(category: number, value: string) {
      if (!this.profile?.id) {
        this.logout();
        return null;
      }

      const { data, statusCode } = await useBackendFetch(
        `/profile/${this.profile.id}/improve`,
      )
        .post({
          category_id: category,
          value,
        })
        .json();

      if (statusCode.value === 401) {
        this.logout();
        return null;
      }

      this.profile.factors[category].improve = value;

      return data.value === 'Saved!';
    },
    async fetchSatisfaction() {
      if (!this.profile?.id) {
        this.logout();
        return null;
      }

      const { data, statusCode } = await useBackendFetch(
        `/profile/${this.profile.id}/satisfied`,
      )
        .get()
        .json();

      if (statusCode.value === 401) {
        this.logout();
        return null;
      }

      return data.value.satisfied;
    },
    async updateSatisfaction(categoryIds: string[]) {
      if (!this.profile?.id) {
        this.logout();
        return null;
      }

      const { data, statusCode } = await useBackendFetch(
        `/profile/${this.profile.id}/satisfied`,
      )
        .post({
          category_ids: categoryIds,
        })
        .json();

      if (statusCode.value === 401) {
        this.logout();
        return null;
      }

      return data.value;
    },
    async fetchGoals() {
      if (!this.profile?.id) {
        this.logout();
        return null;
      }

      const { data, statusCode } = await useBackendFetch(
        `/profile/${this.profile.id}/goals`,
      )
        .get()
        .json();

      if (statusCode.value === 401) {
        this.logout();
        return null;
      }

      return data.value.goals;
    },
    async updateGoals(goals: { category_id: number; vision: number }[]) {
      if (!this.profile?.id) {
        this.logout();
        return null;
      }

      const { data, statusCode } = await useBackendFetch(
        `/profile/${this.profile.id}/goals`,
      )
        .post({ goals })
        .json();

      if (statusCode.value === 401) {
        this.logout();
        return null;
      }

      return data.value;
    },
    async fetchPlannedGoals() {
      if (!this.profile?.id) {
        this.logout();
        return null;
      }

      const { data, statusCode } = await useBackendFetch(
        `/profile/${this.profile.id}/plan`,
      )
        .get()
        .json();

      if (statusCode.value === 401) {
        this.logout();
        return null;
      }

      return data.value.goals;
    },
    async updatePlannedGoals(plan: Record<number, GoalPlan>[]) {
      if (!this.profile?.id) {
        this.logout();
        return null;
      }

      const { data, statusCode } = await useBackendFetch(
        `/profile/${this.profile.id}/plan`,
      )
        .post({ plan })
        .json();

      if (statusCode.value === 401) {
        this.logout();
        return null;
      }

      return data.value;
    },
    async fetchPlan() {
      if (!this.profile?.id) {
        this.logout();
        return null;
      }

      console.log('🔍 Trying to fetch plan data...');
      
      // Use the working endpoint that shows sambandsfaktorer properly
      console.log('🔍 Using working endpoint for sambandsfaktorer...');
      const planData = await this.fetchPlanById(this.profile.id);
      
      if (planData && planData.goals) {
        // Fix the date issue by ensuring each goal has its own date
        const fixedGoals = planData.goals.map((goal: any, index: number) => {
          // Get the correct date for this goal from profile values
          const correctDate = this.getProfileValue(`planWhen${goal.category_id}`);
          console.log(`🔧 Fixing date for goal ${goal.category_id}: ${goal.date} → ${correctDate}`);
          console.log(`🔧 Goal ${goal.category_id} factors before fix:`, goal.factors?.length || 0);
          
          // Use ONLY user's selected factors from plan_goal_factor_targets (goal.factors)
          // Do NOT include affected_by_factors - those are ALL possible connections, not user selections
          const selectedFactors = goal.factors || [];
          
          console.log(`🔧 Goal ${goal.category_id} selected factors:`, selectedFactors.length);

          // Enrich factors with labels from backend data only
          const enrichedFactors = (Array.isArray(selectedFactors) ? selectedFactors : []).map((f: any) => {
            const id = Number(f?.category_id);
            const label = f.label || '';
            const value = f.value != null ? Number(f.value) : 1;
            const vision = f.vision != null ? Number(f.vision) : 5;
            return { ...f, category_id: id || f.category_id, label, value, vision };
          });
          
          return {
            ...goal,
            date: correctDate || goal.date,
            target_date: correctDate || goal.target_date,
            goal_date: correctDate || goal.goal_date,
            due_date: correctDate || goal.due_date,
            // Use only user's selected factors, not all connection factors
            factors: enrichedFactors,
            affected_by_factors: goal.affected_by_factors || [],
            action_plan: goal.action_plan || ''
          };
        });
        
        return {
          ...planData,
          goals: fixedGoals
        };
      }
      
      return planData;
    },

    async fetchFactorsSummary() {
      if (!this.profile?.id) {
        this.logout();
        return null;
      }

      console.log(`🔍 Fetching sambandsfaktorer summary for profile ${this.profile.id}...`);
      
      try {
        const { data, statusCode } = await useBackendFetch(
          `/profile/${this.profile.id}/plan/factors-summary?lang=sv`,
        )
          .get()
          .json();

        console.log(`📡 API Response - Status: ${statusCode.value}`);
        console.log('📡 API Response - Data:', data.value);

        if (statusCode.value === 401) {
          this.logout();
          return null;
        }

        if (statusCode.value === 404) {
          console.error('❌ Factors summary API endpoint not found (404)');
          return null;
        }

        if (statusCode.value !== 200) {
          console.error(`❌ Factors summary API error - Status: ${statusCode.value}`);
          return null;
        }

        console.log('✅ Factors summary received successfully:', data.value);
        return data.value;
      } catch (error) {
        console.error('❌ Error in fetchFactorsSummary:', error);
        return null;
      }
    },

    // New method to fetch complete plan data from behandlingsplan API
    async fetchCompletePlan() {
      if (!this.profile?.id) {
        this.logout();
        return null;
      }
      try {
        const [plannedGoals, baseGoals] = await Promise.all([
          this.fetchPlannedGoals().catch(() => []),
          this.fetchGoals().catch(() => []),
        ]);

        const union = new Map<number, any>();
        const addList = (list: any[]) => {
          (list || []).forEach((g: any) => {
            const id = Number(g?.category_id);
            if (!id) return;
            const prev = union.get(id) || {};
            union.set(id, { ...prev, ...g });
          });
        };

        addList(Array.isArray(plannedGoals) ? plannedGoals : []);
        addList(Array.isArray(baseGoals) ? baseGoals : []);

        if (union.size === 0) {
          return null;
        }

        const baseSelectedIds = (Array.isArray(baseGoals) ? baseGoals : [])
          .filter((g: any) => Number(g.selected) === 1 || String(g.selected) === '1' || g.selected === true)
          .map((g: any) => Number(g.category_id));
        const selectedSet = new Set<number>(baseSelectedIds);

        let goalsForResults = Array.from(union.values()).filter((g: any) => selectedSet.has(Number(g.category_id)));
        if (goalsForResults.length === 0) {
          goalsForResults = Array.from(union.values()).filter((g: any) => Number(g.vision) > Number(g.value));
        }
        if (goalsForResults.length === 0) {
          goalsForResults = Array.from(union.values());
        }

        // Build label map from base goals for reliable category names
        const baseLabelMap = new Map<number, string>();
        (Array.isArray(baseGoals) ? baseGoals : []).forEach((g: any) => {
          const id = Number(g?.category_id);
          if (id) baseLabelMap.set(id, g?.label || '');
        });
        // Build label map from factor summary as an additional source
        let summaryLabelMap = new Map<number, string>();
        try {
          const summary: any = await this.fetchFactorsSummary();
          if (summary) {
            const list: any[] = Array.isArray(summary) ? summary : (Array.isArray(summary?.factors) ? summary.factors : []);
            summaryLabelMap = new Map<number, string>();
            list.forEach((item: any) => {
              const id = Number(item?.category_id);
              if (!id) return;
              const lbl = item?.label || item?.name || '';
              if (lbl && !baseLabelMap.has(id)) baseLabelMap.set(id, lbl);
              if (lbl) summaryLabelMap.set(id, lbl);
            });
          }
        } catch {}

        const completeGoals = await Promise.all(goalsForResults.map(async (goal: any) => {
          const id = Number(goal.category_id);
          const savedDate = this.getProfileValue(`planWhen${id}`);

          // Use action_plan from the goal object (already fetched from getPlannedGoals)
          // No need to fetch separately - it's included in the plan response
          let actionPlan = goal.action_plan || '';
          console.log(`📝 Goal ${id} action_plan from backend:`, actionPlan);

          // Use only backend data - no localStorage fallback for cross-browser consistency
          let factors: any[] = [];
          try {
            const { data: factorsData } = await useBackendFetch(`/profile/${this.profile.id}/plan/${id}/factors`).get().json();
            if (Array.isArray(factorsData.value)) {
              factors = factorsData.value;
            } else if (factorsData.value && Array.isArray((factorsData.value as any).factors)) {
              factors = (factorsData.value as any).factors;
            }
          } catch {
            // Use ONLY user's selected factors (goal.factors), not all connection factors
            factors = goal.factors || [];
          }
          
          // If still empty, use only goal.factors (user's selections), NOT affected_by_factors
          if (!Array.isArray(factors) || factors.length === 0) {
            factors = goal.factors || [];
          }

        const finalFactors = factors.length > 0 ? factors : [];
        const enrichedFactors = finalFactors.map((f: any) => {
          const id = Number(f?.category_id);
          const baseLabel = baseLabelMap.get(id) || '';
          const summaryLabel = summaryLabelMap.get(id) || '';
          let label = f.label || baseLabel || summaryLabel || '';
          const norm = (label || '').toString().trim().toLowerCase();
          if (!label || norm === 'risk' || norm.startsWith('risk ')) {
            label = baseLabel || summaryLabel || label || '';
          }
          // Use values from backend only
          const value = f.value != null ? Number(f.value) : 1;
          const vision = f.vision != null ? Number(f.vision) : 5;
          return { ...f, category_id: id || f.category_id, label, value, vision };
        });

          return {
            category_id: id,
            label: goal.label,
            value: goal.value ?? 1,
            vision: goal.vision ?? 5,
            selected: goal.selected ?? 1,
            date: savedDate || null,
            target_date: savedDate || null,
            goal_date: savedDate || null,
            due_date: savedDate || null,
            action_plan: actionPlan || goal.action_plan || '',
            factors: enrichedFactors,
          };
        }));

        return { goals: completeGoals };
      } catch (error) {
        console.error('❌ Failed to fetch complete plan:', error);
        return null;
      }
    },

    async fetchPlanById(planId: number) {
      const { data, statusCode } = await useBackendFetch(
        `/profile/${planId}/plan?finished=true&lang=sv`,
      )
        .get()
        .json();

      if (statusCode.value === 401) {
        this.logout();
        return null;
      }

      return data.value;
    },
    async getNewPlan() {
      if (this.profile?.id) {
        return;
      }

      const { data, statusCode } = await useBackendFetch(`/profile/create`)
        .post()
        .json();

      if (statusCode.value === 401) {
        this.logout();
        return;
      }

      this.profile = data.value.profile;
      this.savePoints = null;
    },
    async finishPlan() {
      if (!this.profile?.id) {
        return;
      }

      const { data, statusCode } = await useBackendFetch(`/profile/finish`)
        .post()
        .json();

      if (statusCode.value === 401) {
        this.logout();
        return;
      }

      this.profiles = [
        {
          id: this.profile.id,
          created_at: this.profile.created_at,
        },
        ...this.profiles,
      ];

      this.profile = null;
      this.savePoints = null;
      this.feedbackQuestions = [];
    },
    async fetchFeedbackQuestions() {
      // Always fetch fresh from API to ensure we have the latest questions
      const { data, statusCode } = await useBackendFetch(`/feedback`)
        .get()
        .json();

      if (statusCode.value === 401) {
        this.logout();
        return;
      }

      this.feedbackQuestions = data.value;
    },
async saveFeedback(answers: { [key: string]: number | string }) {
  if (!this.profile?.id) {
    console.error('Cannot save feedback: No active profile');
    return { success: false, error: 'No active profile' };
  }

  try {
    // Convert all numeric values to strings to match the backend expectation
    const stringAnswers: Record<string, string> = {};
    Object.keys(answers).forEach(key => {
      // Convert all answers to strings as required by your CreateFeedbackRequest
      stringAnswers[key] = String(answers[key]);
    });
    
    console.log('Sending feedback with answers:', stringAnswers);
    
    const { data, statusCode, error } = await useBackendFetch(
      `/profile/${this.profile.id}/feedback`
    )
      .post({
        answers: stringAnswers
      })
      .json();

    console.log('Feedback save response:', { 
      data: data.value, 
      statusCode: statusCode.value, 
      error: error?.value 
    });

    if (statusCode.value === 401) {
      console.error('Authentication error when saving feedback');
      this.logout();
      return { success: false, error: 'Authentication error' };
    }
    
    // Handle 403 specifically - this means feedback was already submitted
    if (statusCode.value === 403) {
      console.warn('Feedback was already submitted for this profile');
      return { 
        success: false, 
        error: 'Forbidden',
        alreadySubmitted: true,
        message: data.value?.message || 'This action is unauthorized.'
      };
    }

    if (statusCode.value && statusCode.value >= 400) {
      console.error('Error saving feedback, status code:', statusCode.value);
      
      // Handle validation errors
      if (statusCode.value === 422 && data.value && data.value.errors) {
        console.error('Validation errors:', data.value.errors);
        return { 
          success: false, 
          error: 'Validation error', 
          details: data.value.errors 
        };
      }
      
      return { success: false, error: `API error: ${statusCode.value}` };
    }

    return { 
      success: true, 
      data: data.value || {}
    };
  } catch (error) {
    console.error('Exception when saving feedback:', error);
    return { success: false, error: (error as Error).message || 'Unknown error' };
  }
},

    async fetchCategoryAnswers(categoryId: number) {
      console.log(`🔍 Fetching answers for category ${categoryId}`);
      // Always fetch fresh data, don't use cache
      const { data, statusCode } = await useBackendFetch(`/categories/${categoryId}/answers`)
        .get()
        .json();

      console.log(`📡 API response for category ${categoryId}:`, { data: data.value, statusCode: statusCode.value });

      if (statusCode.value === 401) {
        this.logout();
        return {};
      }

      if (data.value?.answers) {
        console.log(`✅ Found answers for category ${categoryId}:`, data.value.answers);
        
        // Transform the nested API response to flat structure
        const flatAnswers: Record<string, string> = {};
        
        // The API returns answers nested under question names
        // We need to flatten it to { "1": "Aldrig", "2": "Sällan", etc. }
        Object.values(data.value.answers).forEach((questionData: any) => {
          if (questionData.answers && Array.isArray(questionData.answers)) {
            questionData.answers.forEach((answer: any) => {
              flatAnswers[answer.value] = answer.label;
            });
          }
        });
        
        console.log(`🔄 Transformed answers for category ${categoryId}:`, flatAnswers);
        this.categoryAnswers[categoryId] = flatAnswers;
        return flatAnswers;
      }

      console.log(`❌ No answers found for category ${categoryId}`);
      return {};
    },

    async saveGoalDate(goalCategoryId: number, date: string, factors: any[] = []) {
      console.log(`🎯 Saving goal date: category ${goalCategoryId} → date ${date}`);
      
      if (!this.profile?.id) {
        console.error('No active profile to save to');
        this.logout();
        return { success: false, error: 'No active profile' };
      }

      // Optimistically cache locally for immediate UX
      const fieldName = `planWhen${goalCategoryId}`;
      if (!this.profile.values) {
        this.profile.values = {} as any;
      }
      this.profile.values[fieldName] = date;
      console.log(`✅ (optimistic) Goal date saved locally: ${fieldName} = ${date}`);

      try {
        const { data, statusCode } = await useBackendFetch(
          `/profile/${this.profile.id}/goal/${goalCategoryId}/date`,
        )
          .post({ date, factors })
          .json();

        if (statusCode?.value === 401) {
          console.error('Authentication error when saving goal date');
          this.logout();
          return { success: false, error: 'Authentication error' };
        }

        if (statusCode?.value && statusCode.value >= 400) {
          console.error(`API error ${statusCode.value} when saving goal date`);
          // Keep optimistic value
          return { success: false, error: `API error: ${statusCode.value}` };
        }

        if (!data.value) {
          console.error('No data returned from goal date API', data);
          // Keep optimistic value
          return { success: false, error: 'No data returned from API' };
        }

        console.log('✅ Goal date API response:', data.value);
        return { success: true, data: data.value };

      } catch (error) {
        console.error('❌ Error saving goal date:', error);
        // Keep optimistic value
        return { success: false, error: (error as Error).message || 'Unknown error' };
      }
    },

    async updateGoalPlan(goalCategoryId: number, planText: string, factors: any[], visions?: Record<number, number>) {
      console.log(`🔄 Updating goal plan for category ${goalCategoryId}`);
      console.log(`📝 Action plan text (${planText.length} chars):`, planText);
      console.log(`🎯 Selected factors:`, factors);
      console.log(`🎯 Visions data:`, visions);
      
      if (!this.profile?.id) {
        this.logout();
        return { success: false, error: 'No active profile' };
      }

      // Transform factors array to the format backend expects
      // Use real risk values so user can see their actual starting point
      const formattedFactors = factors.map(categoryId => {
        // Get current value from profile factors, ensuring it's a valid integer 1-5
        const factorData = this.profile?.factors?.[categoryId];
        let currentValue = factorData?.value ?? factorData?.risk_value ?? 1;
        
        // Ensure value is a valid integer between 1-5
        currentValue = Math.max(1, Math.min(5, Math.floor(Number(currentValue) || 1)));
        
        // Get target value, ensure it's valid and >= current value
        let targetValue = visions?.[categoryId] ?? (currentValue + 1);
        targetValue = Math.max(1, Math.min(5, Math.floor(Number(targetValue) || currentValue + 1)));
        
        console.log(`📊 Factor ${categoryId}: value=${currentValue}, vision=${targetValue}`);
        
        return {
          category_id: Number(categoryId),
          value: currentValue,
          vision: targetValue
        };
      });
      
      const payload = {
        action_plan: planText,
        selected_factors: formattedFactors
      };
      console.log(`📤 Sending payload:`, payload);

      // Data is saved to database - no localStorage caching needed for cross-browser consistency

      const { data, statusCode } = await useBackendFetch(
        `/profile/${this.profile.id}/plan/${goalCategoryId}`,
      )
        .post(payload)
        .json();

      console.log(`📡 updateGoalPlan Response Status: ${statusCode.value}`);
      console.log(`📡 updateGoalPlan Response Data:`, data.value);

      console.log(`📡 Update goal plan response:`, { data: data.value, statusCode: statusCode.value });

      if (statusCode?.value === 401) {
        this.logout();
        return { success: false, error: 'Unauthorized' };
      }

      if (statusCode?.value && statusCode.value >= 400) {
        console.error(`❌ Failed to update goal plan: ${statusCode.value}`);
        return { success: false, error: `HTTP ${statusCode.value}` };
      }

      console.log(`✅ Goal plan updated successfully for category ${goalCategoryId}`);
      // Data is persisted in database - results page will fetch fresh from API
      return { success: true, data: data.value };
    },

    async fetchFactorsForGoal(goalId: number) {
      console.log(`🔍 Fetching factors for goal ${goalId}`);
      
      const { data, statusCode } = await useBackendFetch(`/profile/${this.profile?.id}/plan/${goalId}/factors`)
        .get()
        .json();

      console.log(`📡 Fetch factors response:`, { data: data.value, statusCode: statusCode.value });

      if (statusCode.value === 401) {
        this.logout();
        return [];
      }

      if (statusCode.value && statusCode.value >= 400) {
        console.error(`❌ Failed to fetch factors: ${statusCode.value}`);
        return [];
      }

      let result: any = [];
      if (Array.isArray(data.value)) {
        result = data.value;
      } else if (data.value && Array.isArray((data.value as any).factors)) {
        result = (data.value as any).factors;
      } else if (data.value && Array.isArray((data.value as any).factor_categories)) {
        result = (data.value as any).factor_categories.map((it: any) => {
          const id = Number(it?.category_id ?? it?.id);
          const value = typeof it?.current_value === 'number' ? it.current_value : undefined;
          return {
            category_id: id,
            label: it?.label ?? it?.name ?? '',
            value,
            is_risk: it?.is_risk,
          };
        });
      } else {
        result = [];
      }

      console.log(`✅ Factors fetched successfully for goal ${goalId}:`, result);
      return result;
    },

    async fetchAnswerTexts(categoryId: number) {
      console.log(`🔍 Fetching answer texts for category ${categoryId}`);
      
      const { data, statusCode } = await useBackendFetch(`/categories/${categoryId}/answers`)
        .get()
        .json();

      console.log(`📡 Fetch answer texts response:`, { data: data.value, statusCode: statusCode.value });

      if (statusCode.value === 401) {
        this.logout();
        return {};
      }

      if (statusCode.value && statusCode.value >= 400) {
        console.error(`❌ Failed to fetch answer texts: ${statusCode.value}`);
        return {};
      }

      if (data.value?.answers) {
        console.log(`✅ Answer texts found for category ${categoryId}:`, data.value.answers);
        this.answerTexts[categoryId] = data.value.answers;
        return data.value.answers;
      }

      console.log(`❌ No answer texts found for category ${categoryId}`);
      return {};
    },
  },
});
